"use client"

import { ChevronRight, type LucideIcon } from "lucide-react"
import { useState, useEffect } from "react"
import { usePathname, useRouter } from "next/navigation"
import Link from "next/link"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible"
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "../ui/sidebar"

export function NavMain({
  items,
  onSettingsClick,
}: {
  items: {
    title: string
    url?: string
    icon?: LucideIcon
    isActive?: boolean
    /** Section label (e.g. "Core", "Tools", "Verticals"). Consecutive items
     *  sharing a category render under one header; empty/undefined → no label. */
    category?: string
    items?: {
      title: string
      url: string
      icon?: LucideIcon
      onClick?: () => void
    }[]
    onClick?: () => void
  }[]
  onSettingsClick?: () => void
}) {
  const pathname = usePathname()
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({})
  const [isClient, setIsClient] = useState(false)

  // ── Cross-subdomain cookie helpers ────────────────────────────────
  // Separate key from the sidebar collapsed/expanded boolean (AskFinz_sidebar_state)
  const COOKIE_KEY = "AskFinz_sidebar_sections"
  const LOCAL_KEY = "AskFinz_sidebar_open_items"

  const readCookie = (): Record<string, boolean> | null => {
    if (typeof document === "undefined") return null
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${COOKIE_KEY}=([^;]*)`))
    if (!match) return null
    try {
      return JSON.parse(decodeURIComponent(match[1]))
    } catch {
      return null
    }
  }

  const writeCookie = (state: Record<string, boolean>) => {
    if (typeof document === "undefined") return
    const maxAge = 365 * 24 * 60 * 60 // 1 year
    let domainAttr = ""
    if (typeof window !== "undefined" && window.location.hostname.endsWith("askfinz.ai")) {
      domainAttr = "; Domain=.askfinz.ai"
    }
    const encoded = encodeURIComponent(JSON.stringify(state))
    document.cookie = `${COOKIE_KEY}=${encoded}; path=/; max-age=${maxAge}; SameSite=Lax${domainAttr}`
  }

  useEffect(() => {
    setIsClient(true)

    // Build default state (active items are open)
    const defaultState: Record<string, boolean> = {}
    items.forEach((item) => {
      defaultState[item.title] = item.isActive || false
    })

    // Load persisted state: cookie first (cross-subdomain), then localStorage fallback
    let saved: Record<string, boolean> | null = null

    if (typeof window !== "undefined") {
      saved = readCookie()
      if (!saved) {
        const local = localStorage.getItem(LOCAL_KEY)
        if (local) {
          try { saved = JSON.parse(local) } catch { /* ignore */ }
        }
      }
    }

    if (saved && typeof saved === "object") {
      // Merge: default state as base, then saved preferences, then force-open active section
      const merged = { ...defaultState, ...saved }
      // Always ensure the active section is open
      items.forEach((item) => {
        if (item.isActive) merged[item.title] = true
      })
      setOpenItems(merged)
    } else {
      setOpenItems(defaultState)
    }
  }, [items])

  // Persist to both localStorage and cross-subdomain cookie on every change
  useEffect(() => {
    if (isClient && typeof window !== "undefined" && Object.keys(openItems).length > 0) {
      try {
        localStorage.setItem(LOCAL_KEY, JSON.stringify(openItems))
        writeCookie(openItems)
      } catch {
        // silent
      }
    }
  }, [openItems, isClient])

  const toggleItem = (title: string) => {
    setOpenItems((prev) => ({
      ...prev,
      [title]: !prev[title],
    }))
  }

  // Render a single nav item: a Settings/onClick button, a plain link, or a
  // collapsible app group. Non-active apps are dimmed (Tier 2) so the current
  // app's group stays bright (Tier 1).
  const renderItem = (item: (typeof items)[number]) => {
    const isSettings = item.title === "Settings"
    const hasValidOnClick = item.onClick && typeof item.onClick === "function"
    // Dim app groups that aren't the current app. Settings/links keep full opacity.
    const dimClass = item.items && item.isActive === false
      ? "opacity-60 transition-opacity hover:opacity-100"
      : undefined

    if (isSettings || hasValidOnClick) {
      const handleClick = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        // Prioritize Settings handler
        if (isSettings && onSettingsClick) {
          onSettingsClick()
        } else if (hasValidOnClick && item.onClick) {
          item.onClick()
        }
      }

      return (
        <SidebarMenuItem key={item.title}>
          <SidebarMenuButton
            tooltip={item.title}
            onClick={handleClick}
          >
            {item.icon && <item.icon className="group-data-[collapsible=icon]:flex-shrink-0" />}
            <span>{item.title}</span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      )
    }

    // If item has URL and no items, render as link
    if (item.url && !item.items) {
      return (
        <SidebarMenuItem key={item.title}>
          <SidebarMenuButton asChild tooltip={item.title} className={dimClass}>
            <Link href={item.url}>
              {item.icon && <item.icon className="group-data-[collapsible=icon]:flex-shrink-0" />}
              <span>{item.title}</span>
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      )
    }

    // Otherwise, render as collapsible app group
    return (
      <Collapsible
        key={item.title}
        asChild
        open={openItems[item.title] || false}
        onOpenChange={() => toggleItem(item.title)}
        className="group/collapsible"
      >
        <SidebarMenuItem>
          <CollapsibleTrigger asChild>
            <SidebarMenuButton tooltip={item.title} className={dimClass}>
              {item.icon && <item.icon className="group-data-[collapsible=icon]:flex-shrink-0" />}
              <span>{item.title}</span>
              {item.items && (
                <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 group-data-[collapsible=icon]:hidden" />
              )}
            </SidebarMenuButton>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarMenuSub>
              {item.items?.map((subItem) => {
                const handleSubClick = (subItem.onClick || subItem.title === "Settings")
                  ? (e: React.MouseEvent) => {
                      if (subItem.title === "Settings") {
                        e.preventDefault(); onSettingsClick?.(); return
                      }
                      subItem.onClick?.()
                      // If there's also a URL, navigate after firing the callback
                      if (subItem.url) {
                        window.location.href = subItem.url
                      }
                    }
                  : undefined

                return (
                  <SidebarMenuSubItem key={subItem.title}>
                    {handleSubClick ? (
                      <SidebarMenuSubButton onClick={handleSubClick} className="flex items-center gap-1.5">
                        {subItem.icon && <subItem.icon className="size-3.5 shrink-0 text-sidebar-foreground/70" />}
                        <span>{subItem.title}</span>
                      </SidebarMenuSubButton>
                    ) : (
                      <SidebarMenuSubButton asChild>
                        <Link href={subItem.url} className="flex items-center gap-1.5">
                          {subItem.icon && <subItem.icon className="size-3.5 shrink-0 text-sidebar-foreground/70" />}
                          <span>{subItem.title}</span>
                        </Link>
                      </SidebarMenuSubButton>
                    )}
                  </SidebarMenuSubItem>
                )
              })}
            </SidebarMenuSub>
          </CollapsibleContent>
        </SidebarMenuItem>
      </Collapsible>
    )
  }

  // Group items by consecutive category so each section renders under its own
  // label (Core / Tools / Verticals). Items with no category (Home,
  // navSecondary) render in a label-less group.
  const groups: { label: string; items: typeof items }[] = []
  for (const it of items) {
    const label = it.category ?? ""
    const last = groups[groups.length - 1]
    if (last && last.label === label) last.items.push(it)
    else groups.push({ label, items: [it] })
  }

  return (
    <>
      {groups.map((g, gi) => (
        <SidebarGroup key={g.label || `__nolabel_${gi}`} className="py-1">
          {g.label && <SidebarGroupLabel>{g.label}</SidebarGroupLabel>}
          <SidebarMenu>
            {g.items.map(renderItem)}
          </SidebarMenu>
        </SidebarGroup>
      ))}
    </>
  )
}

